import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { View } from '@actual-app/components/view';

import { useCerrarConAtras } from '#components/mobile/ui/atras';
import { Icono } from '#components/mobile/ui/Icono';
import {
  color,
  densidad,
  espacio,
  radio,
  sombra,
  TACTIL,
  texto,
} from '#components/mobile/ui/tokens';

import { KEYPAD_Z_INDEX } from './AssignKeypad';
import { ESTADOS_FILTRO, indiceEnCarril } from './filtrosCategoriasLogica';
import type {
  EjesFiltro,
  EstadoFiltro,
  FiltroGuardado,
} from './filtrosCategoriasLogica';

/** El carril del gatillo, en fracción del alto de la pantalla. */
export const CARRIL_ARRIBA = 0.22;
export const CARRIL_ABAJO = 0.74;
/** Alto de la lengüeta. */
export const ALTO_GATILLO = 96;
/** Lo que se ve de la lengüeta (el resto de la zona táctil es invisible). */
const ANCHO_VISIBLE = 18;
/** Pulsación larga: filtros guardados. */
const MS_LARGA = 480;
/** Lo que hay que mover el dedo para que cuente como deslizar. */
const UMBRAL = 6;

const Z = KEYPAD_Z_INDEX - 3;

type GatilloDineroProps = {
  estado: EstadoFiltro;
  nombres: Record<EstadoFiltro, string>;
  /** Cuántas salen con cada opción (con la cuenta y el gasto puestos). */
  conteos: Record<EstadoFiltro, number>;
  onEstado: (estado: EstadoFiltro) => void;
  guardados: readonly FiltroGuardado[];
  /** Id del guardado que coincide con lo puesto, si lo hay. */
  enUso: string | null;
  /** Hay algún eje puesto (si no, no se ofrece «Guardar este filtro»). */
  filtrando: boolean;
  cuantasCon: (ejes: EjesFiltro) => number;
  onAplicar: (filtro: FiltroGuardado) => void;
  onGuardar: (nombre: string) => void;
  onBorrar: (id: string) => void;
  onPagosFijos: () => void;
  /** Escondido (p. ej. con el teclado de asignar abierto). */
  oculto?: boolean;
};

/**
 * Gatillo de lado del Plan (Vadik, 22-oct-2026): una lengüeta de latón en el
 * borde derecho. Se aprieta y se desliza: al lado del dedo salen las
 * opciones de dinero con cuántas hay y la lista se filtra mientras se
 * desliza; al soltar se queda. Un toque vuelve a «Todas». Mantenerla pulsada
 * abre los filtros guardados.
 */
export function GatilloDinero({
  estado,
  nombres,
  conteos,
  onEstado,
  guardados,
  enUso,
  filtrando,
  cuantasCon,
  onAplicar,
  onGuardar,
  onBorrar,
  onPagosFijos,
  oculto = false,
}: GatilloDineroProps) {
  const { t } = useTranslation();
  const [deslizando, setDeslizando] = useState(false);
  const [yDedo, setYDedo] = useState<number | null>(null);
  const [verGuardados, setVerGuardados] = useState(false);
  const gesto = useRef<{
    y0: number;
    modo: 'espera' | 'desliza' | 'larga';
    temporizador: number;
  } | null>(null);
  // Lo último elegido, para no repetir onEstado ni la vibración.
  const ultimo = useRef(estado);
  ultimo.current = estado;

  useEffect(
    () => () => {
      if (gesto.current) {
        window.clearTimeout(gesto.current.temporizador);
      }
    },
    [],
  );

  if (oculto) {
    return null;
  }

  const alto = window.innerHeight;
  const arriba = alto * CARRIL_ARRIBA;
  const abajo = alto * CARRIL_ABAJO;
  const elegir = (y: number) => {
    const e =
      ESTADOS_FILTRO[indiceEnCarril(y, arriba, abajo, ESTADOS_FILTRO.length)];
    if (e !== ultimo.current) {
      ultimo.current = e;
      vibrar();
      onEstado(e);
    }
  };

  const onPointerDown = (ev: ReactPointerEvent<HTMLDivElement>) => {
    ev.currentTarget.setPointerCapture(ev.pointerId);
    const temporizador = window.setTimeout(() => {
      if (gesto.current?.modo === 'espera') {
        gesto.current.modo = 'larga';
        vibrar();
        setVerGuardados(true);
      }
    }, MS_LARGA);
    gesto.current = { y0: ev.clientY, modo: 'espera', temporizador };
  };
  const onPointerMove = (ev: ReactPointerEvent<HTMLDivElement>) => {
    const g = gesto.current;
    if (!g) {
      return;
    }
    if (g.modo === 'espera' && Math.abs(ev.clientY - g.y0) > UMBRAL) {
      g.modo = 'desliza';
      window.clearTimeout(g.temporizador);
      setDeslizando(true);
    }
    if (g.modo === 'desliza') {
      setYDedo(ev.clientY);
      elegir(ev.clientY);
    }
  };
  const onPointerUp = () => {
    const g = gesto.current;
    gesto.current = null;
    if (!g) {
      return;
    }
    window.clearTimeout(g.temporizador);
    if (g.modo === 'espera') {
      // Un toque: vuelta a «Todas».
      if (ultimo.current !== 'ninguno') {
        onEstado('ninguno');
      }
    }
    setDeslizando(false);
    setYDedo(null);
  };

  // La lengüeta sigue al dedo dentro del carril; quieta, a media pantalla.
  const centro =
    yDedo != null
      ? Math.max(arriba, Math.min(abajo, yDedo))
      : alto * ((CARRIL_ARRIBA + CARRIL_ABAJO) / 2);
  const rotulo = estado === 'ninguno' ? t('Money') : nombres[estado];

  return createPortal(
    <>
      {deslizando && (
        <View
          aria-hidden
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: Z,
            pointerEvents: 'none',
            background: `linear-gradient(90deg, transparent 35%, ${color.bg} 92%)`,
            opacity: 0.88,
          }}
        />
      )}
      {deslizando &&
        ESTADOS_FILTRO.map((e, i) => {
          const y =
            arriba + ((abajo - arriba) * i) / (ESTADOS_FILTRO.length - 1);
          const sel = e === estado;
          const cerca = Math.abs(ESTADOS_FILTRO.indexOf(estado) - i) === 1;
          const rojo = e === 'gastado-de-mas';
          return (
            <span
              key={e}
              aria-hidden
              data-testid={`gatillo-opcion-${e}`}
              style={{
                position: 'fixed',
                right: 50,
                top: y,
                zIndex: Z + 2,
                pointerEvents: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                padding: '6px 12px',
                borderRadius: radio.pildora,
                ...densidad.nombre,
                fontWeight: 800,
                whiteSpace: 'nowrap',
                boxShadow: sombra.flotante,
                backgroundColor: sel
                  ? rojo
                    ? color.bad
                    : color.fg
                  : color.surface,
                color: sel ? color.bg : rojo ? color.bad : color.fg2,
                opacity: !sel && conteos[e] === 0 ? 0.5 : 1,
                transform: `translateY(-50%) translateX(${sel ? -14 : cerca ? -5 : 0}px) scale(${sel ? 1.12 : cerca ? 1 : 0.92})`,
                transformOrigin: 'right center',
                transition:
                  'transform 220ms cubic-bezier(.3, 1.4, .5, 1), background-color 120ms',
              }}
            >
              {nombres[e]}
              <span style={{ fontWeight: 600, opacity: 0.7 }}>
                {conteos[e]}
              </span>
            </span>
          );
        })}
      <div
        role="slider"
        tabIndex={0}
        aria-label={t('Money filter: {{value}}', { value: rotulo })}
        aria-valuemin={0}
        aria-valuemax={ESTADOS_FILTRO.length - 1}
        aria-valuenow={ESTADOS_FILTRO.indexOf(estado)}
        aria-valuetext={rotulo}
        data-testid="gatillo-dinero"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={ev => {
          const i = ESTADOS_FILTRO.indexOf(estado);
          if (ev.key === 'ArrowDown' && i < ESTADOS_FILTRO.length - 1) {
            onEstado(ESTADOS_FILTRO[i + 1]);
          } else if (ev.key === 'ArrowUp' && i > 0) {
            onEstado(ESTADOS_FILTRO[i - 1]);
          }
        }}
        style={{
          position: 'fixed',
          right: 0,
          top: centro - ALTO_GATILLO / 2,
          width: TACTIL,
          height: ALTO_GATILLO,
          zIndex: Z + 1,
          touchAction: 'none',
          cursor: 'grab',
          display: 'flex',
          justifyContent: 'flex-end',
          WebkitTapHighlightColor: 'transparent',
          transition: yDedo == null ? 'top 250ms ease' : undefined,
        }}
      >
        <span
          aria-hidden
          style={{
            width: deslizando ? ANCHO_VISIBLE + 6 : ANCHO_VISIBLE,
            height: '100%',
            borderRadius: '12px 0 0 12px',
            background:
              'linear-gradient(90deg, #9b6b22, #d4a548 35%, #f7dc95 62%, #d4a548)',
            boxShadow:
              '-2px 3px 8px rgba(60, 40, 10, 0.3), inset 1px 0 0 rgba(255, 240, 200, 0.6)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            transition: 'width 150ms',
          }}
        >
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[0, 1, 2].map(k => (
              <span
                key={k}
                style={{
                  width: 9,
                  height: 2,
                  borderRadius: 2,
                  backgroundColor: 'rgba(90, 58, 16, 0.55)',
                  boxShadow: '0 1px 0 rgba(255, 240, 200, 0.6)',
                }}
              />
            ))}
          </span>
          <span
            style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              fontSize: 9.5,
              fontWeight: 800,
              letterSpacing: '0.03em',
              color: '#3a2608',
              whiteSpace: 'nowrap',
              maxHeight: ALTO_GATILLO - 30,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {rotulo}
          </span>
        </span>
      </div>
      {verGuardados && (
        <Guardados
          top={centro}
          guardados={guardados}
          enUso={enUso}
          filtrando={filtrando}
          cuantasCon={cuantasCon}
          onAplicar={f => {
            setVerGuardados(false);
            onAplicar(f);
          }}
          onGuardar={nombre => {
            setVerGuardados(false);
            onGuardar(nombre);
          }}
          onBorrar={onBorrar}
          onPagosFijos={() => {
            setVerGuardados(false);
            onPagosFijos();
          }}
          onCerrar={() => setVerGuardados(false)}
        />
      )}
    </>,
    document.body,
  );
}

type GuardadosProps = {
  top: number;
  guardados: readonly FiltroGuardado[];
  enUso: string | null;
  filtrando: boolean;
  cuantasCon: (ejes: EjesFiltro) => number;
  onAplicar: (filtro: FiltroGuardado) => void;
  onGuardar: (nombre: string) => void;
  onBorrar: (id: string) => void;
  onPagosFijos: () => void;
  onCerrar: () => void;
};

/** Lista de filtros guardados junto a la lengüeta (pulsación larga). */
function Guardados({
  top,
  guardados,
  enUso,
  filtrando,
  cuantasCon,
  onAplicar,
  onGuardar,
  onBorrar,
  onPagosFijos,
  onCerrar,
}: GuardadosProps) {
  const { t } = useTranslation();
  // «Atrás» del móvil cierra la lista (no cambia de pestaña).
  useCerrarConAtras(true, onCerrar);
  const [nombrando, setNombrando] = useState(false);
  const [nombre, setNombre] = useState('');
  const fila = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    minHeight: TACTIL,
    padding: '0 10px',
    borderRadius: radio.sm,
    textAlign: 'left',
    backgroundColor: 'transparent',
    ...densidad.nombre,
  } as const;
  return (
    <>
      <View
        aria-hidden
        onClick={onCerrar}
        style={{ position: 'fixed', inset: 0, zIndex: Z + 3 }}
      />
      <View
        role="dialog"
        aria-label={t('Saved filters')}
        data-testid="filtros-guardados"
        style={{
          position: 'fixed',
          right: 48,
          top: Math.max(
            espacio.margen,
            Math.min(window.innerHeight - 340, top - 120),
          ),
          width: 236,
          maxHeight: 340,
          overflowY: 'auto',
          zIndex: Z + 4,
          padding: 8,
          borderRadius: radio.tarjeta,
          backgroundColor: color.surfaceHoja,
          boxShadow: sombra.flotante,
        }}
      >
        <span
          style={{
            ...texto.etiqueta,
            color: color.fg3,
            padding: '6px 10px 4px',
          }}
        >
          <Trans>Saved filters</Trans>
        </span>
        {guardados.length === 0 && (
          <span
            style={{
              ...densidad.pequeno,
              color: color.fg3,
              padding: '4px 10px 8px',
            }}
          >
            <Trans>
              No saved filters yet. Set the filters and save them here.
            </Trans>
          </span>
        )}
        {guardados.map(f => (
          <View
            key={f.id}
            style={{ flexDirection: 'row', alignItems: 'center' }}
          >
            <Button
              variant="bare"
              onPress={() => onAplicar(f)}
              data-testid="filtro-guardado"
              aria-pressed={f.id === enUso}
              style={{
                ...fila,
                flex: 1,
                minWidth: 0,
                fontWeight: f.id === enUso ? 800 : 600,
                color: f.id === enUso ? color.accent : color.fg,
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {f.nombre}
              </span>
              <span style={{ color: color.fg3, fontWeight: 600 }}>
                {cuantasCon(f)}
              </span>
            </Button>
            <Button
              variant="bare"
              aria-label={t('Delete filter {{name}}', { name: f.nombre })}
              data-testid="filtro-guardado-borrar"
              onPress={() => onBorrar(f.id)}
              style={{
                width: TACTIL,
                height: TACTIL,
                justifyContent: 'center',
                alignItems: 'center',
                color: color.fg3,
                backgroundColor: 'transparent',
              }}
            >
              <Icono nombre="x" size={14} />
            </Button>
          </View>
        ))}
        {filtrando && !nombrando && (
          <Button
            variant="bare"
            onPress={() => setNombrando(true)}
            data-testid="filtro-guardar-este"
            style={{
              ...fila,
              justifyContent: 'flex-start',
              color: color.accent,
              fontWeight: 700,
            }}
          >
            <Icono nombre="plus" size={16} />
            <Trans>Save this filter</Trans>
          </Button>
        )}
        {nombrando && (
          <form
            onSubmit={ev => {
              ev.preventDefault();
              if (nombre.trim()) {
                onGuardar(nombre);
              }
            }}
            style={{ display: 'flex', gap: 6, padding: '4px 4px 6px' }}
          >
            <Input
              autoFocus
              value={nombre}
              aria-label={t('Filter name')}
              placeholder={t('Filter name')}
              data-testid="filtro-guardado-nombre"
              onChangeValue={setNombre}
              style={{
                flex: 1,
                minWidth: 0,
                height: 40,
                fontSize: 15,
                borderRadius: radio.sm,
                backgroundColor: color.surface2,
                color: color.fg,
                border: `1px solid ${color.line}`,
                padding: '0 10px',
              }}
            />
            <Button
              type="submit"
              variant="bare"
              isDisabled={!nombre.trim()}
              data-testid="filtro-guardado-ok"
              style={{
                height: 40,
                padding: '0 12px',
                borderRadius: radio.sm,
                backgroundColor: color.accent,
                color: color.accentInk,
                fontWeight: 800,
              }}
            >
              <Trans>Save</Trans>
            </Button>
          </form>
        )}
        <Button
          variant="bare"
          onPress={onPagosFijos}
          data-testid="filtro-pagos-fijos"
          style={{
            ...fila,
            justifyContent: 'flex-start',
            color: color.fg2,
            borderTop: `1px solid ${color.line}`,
            borderRadius: 0,
            marginTop: 4,
          }}
        >
          <Icono nombre="repeat" size={16} />
          <Trans>Mark fixed payments…</Trans>
        </Button>
      </View>
    </>
  );
}

function vibrar() {
  try {
    navigator.vibrate?.(8);
  } catch {
    // Sin vibración (escritorio o navegador que no la deja): nada.
  }
}
